// @ts-nocheck
/**
 * Team lookup against RecruitLook.
 *
 * RecruitLook owns teams permanently; RLevents holds none of its own. Every
 * search, creation, read and rename goes through the `rl-teams` proxy, and the
 * answers live in memory for the session only — nothing is persisted, and
 * there is deliberately no stale local fallback when RL is unavailable.
 */
import { supabase } from "@/integrations/supabase/client";
import { cleanTeamName } from "@/lib/teamNameClean";

export interface RlTeam {
  id: string;
  name: string;
  display_name: string;
  canonical_name: string;
  age_group: string | null;
  gender: string | null;
  club_id: string | null;
  club_name: string | null;
  aliases: string[];
  event_count: number;
  team_url: string | null;
}

export class RlAuthRequiredError extends Error {
  code = "auth_required";
  constructor(message = "Sign in to find your team.") {
    super(message);
  }
}

async function call(body: Record<string, unknown>) {
  const { data, error } = await supabase.functions.invoke("rl-teams", { body });
  if (error) {
    // A non-2xx answer keeps its body on error.context — read it so the caller
    // can tell "sign in first" apart from an outage.
    const payload = await (error as any)?.context?.json?.().catch(() => null);
    if (payload?.code === "auth_required" || (error as any)?.context?.status === 401) {
      throw new RlAuthRequiredError(payload?.error);
    }
    throw new Error(payload?.error || error.message || "Couldn't reach RecruitLook.");
  }
  if (!data?.ok) {
    if (data?.code === "auth_required") throw new RlAuthRequiredError(data?.error);
    throw new Error(data?.error || "RecruitLook didn't answer.");
  }
  return data;
}

/** Session cache for teams already fetched by id. Memory only. */
const teamCache = new Map<string, RlTeam>();

export function cachedTeam(id: string | null | undefined): RlTeam | null {
  return id ? teamCache.get(String(id)) ?? null : null;
}

/** Signed-in only — the directory is not public. */
export async function searchRlTeams(q: string, limit = 8): Promise<RlTeam[]> {
  const data = await call({ action: "search", q, limit });
  const teams = (data.teams ?? []) as RlTeam[];
  for (const t of teams) teamCache.set(t.id, t);
  return teams;
}


/**
 * The signed-in coach's own RecruitLook teams. RL reads the user from the JWT.
 * Never throws — returns [] (and logs) on any failure.
 */
export async function fetchMyRlTeams(): Promise<RlTeam[]> {
  try {
    const { data, error } = await supabase.functions.invoke("rl-teams", {
      body: { action: "mine" },
    });
    if (error || !data?.ok) {
      console.error("rl-teams mine failed", error ?? data);
      return [];
    }
    const teams = (Array.isArray(data.teams) ? data.teams : []) as RlTeam[];
    for (const t of teams) if (t?.id) teamCache.set(String(t.id), t);
    return teams;
  } catch (err) {
    console.error("rl-teams mine failed", err);
    return [];
  }
}

/** Batched read — one request for every team on an event. */
export async function getRlTeams(
  ids: string[],
  { force = false }: { force?: boolean } = {}
): Promise<Record<string, RlTeam>> {
  const wanted = [...new Set(ids.filter(Boolean).map(String))];
  const out: Record<string, RlTeam> = {};
  const missing: string[] = [];
  for (const id of wanted) {
    const hit = !force && teamCache.get(id);
    if (hit) out[id] = hit;
    else missing.push(id);
  }
  if (missing.length === 0) return out;

  const data = await call({ action: "get", ids: missing });
  const teams = (data.teams ?? {}) as Record<string, RlTeam>;
  for (const [id, t] of Object.entries(teams)) {
    teamCache.set(id, t);
    out[id] = t;
  }
  return out;
}

/** The RecruitLook id carried by an RLE registration (legacy rows use team_id). */
export function registrationTeamId(row: any): string {
  return String(row?.recruitlook_team_id || row?.team_id || "");
}

/**
 * Permanent, non-throwing registration snapshot fallback. RecruitLook can be
 * unavailable without making a registration, payment or event screen fail.
 */
export function storedRegistrationTeamName(row: any, fallback = "Team"): string {
  return String(
    row?.display_name_override ||
      row?.team_name ||
      row?.extras_summary?.team_name ||
      fallback
  );
}

/** Resolve names in one RecruitLook request and always preserve stored labels. */
export async function resolveRegistrationTeamNames(
  rows: any[],
  fallback = "Team"
): Promise<Record<string, string>> {
  const names: Record<string, string> = {};
  for (const row of rows) names[registrationTeamId(row)] = storedRegistrationTeamName(row, fallback);
  try {
    const resolved = await getRlTeams(rows.map(registrationTeamId).filter(Boolean));
    for (const [id, team] of Object.entries(resolved)) {
      names[id] = team.display_name || team.name || names[id] || fallback;
    }
  } catch {
    // Deliberately retain the registration snapshot when RL/auth is unavailable.
  }
  return names;
}

/**
 * Last line of defence for the standing rule: whatever a caller passes, the
 * name written to RecruitLook carries no age group, grad year, grade or gender
 * word. Anything stripped fills the matching field instead.
 */
function sanitiseTeamWrite(patch: {
  name?: string;
  age_group?: string | null;
  gender?: string | null;
}) {
  if (patch.name === undefined) return patch;
  const cleaned = cleanTeamName(patch.name);
  return {
    ...patch,
    name: cleaned.name || patch.name,
    age_group: patch.age_group || cleaned.ageGroup || null,
    gender: patch.gender || cleaned.gender || null,
  };
}

/**
 * Create a team on RecruitLook. RL returns the existing record when one
 * already matched, so this can never produce a duplicate.
 */
export async function createRlTeam(input: {
  name: string;
  age_group?: string | null;
  gender?: string | null;
  raw_input?: string | null;
}): Promise<RlTeam> {
  const data = await call({ action: "create", ...sanitiseTeamWrite(input) });
  const team = data.team as RlTeam;
  teamCache.set(team.id, team);
  return team;
}

/** Rename / correct a team's permanent record — RL owns the write. */
export async function updateRlTeam(
  teamId: string,
  patch: { name?: string; age_group?: string | null; gender?: string | null }
): Promise<RlTeam> {
  const data = await call({
    action: "update",
    team_id: teamId,
    ...sanitiseTeamWrite(patch),
  });
  const team = data.team as RlTeam;
  if (team?.id) teamCache.set(team.id, team);
  return team;
}
